import { Avatar, theme, Typography } from 'antd'
import dayjs from 'dayjs'
import { isValidElement } from 'react'

import { DATE_FORMAT, DATETIME_FORMAT } from '@/Ship/Configs/DateFormat'
import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'
import { classNames } from '@/Ship/Helpers/classNames'
import { formatMoney, formatQuantity } from '@/Ship/Helpers/formatNumber'

import type { FC, ReactNode } from 'react'

import styles from '@/Ship/Components/EntityCard/EntityCard.module.css'

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: unknown; children?: ReactNode }>

/** Функція `view` поля ресурсу — так, як її кличе ядро. */
export type FieldView = (arg1: unknown, formContext?: unknown) => ReactNode

interface ViewObjectArg {
    field?: unknown
    formContext?: unknown
    value?: unknown
}

const isViewObjectArg = (arg: unknown): arg is ViewObjectArg =>
    arg !== null && typeof arg === 'object' && !Array.isArray(arg) && 'value' in (arg as object)

/**
 * Загорнути показ значення в подвійний контракт `view` ядра.
 *
 * Ядро кличе `view` двічі: спершу **позиційно** (`view(value, formContext)`),
 * де треба порахувати показ, а потім **об'єктом** (`view({ field, value })`),
 * де `value` — вже результат першого виклику, і його треба лише загорнути в
 * шаблон із підписом (для полів із власним `view` ядро шаблон не додає).
 *
 * Кожен показ до цього повторював цю розвилку сам. Тут вона одна.
 */
export const defineView = (render: (value: unknown, formContext?: unknown) => ReactNode): FieldView => {
    return (arg1, formContext) => {
        if (isValidElement(arg1)) return arg1

        if (isViewObjectArg(arg1)) {
            const value = arg1.value
            const content =
                isValidElement(value) || typeof value === 'string' ? value : render(value, arg1.formContext)

            if (!arg1.field) return content

            return <ViewFieldTemplate field={arg1.field}>{content}</ViewFieldTemplate>
        }

        return render(arg1, formContext)
    }
}

// ---- Люди --------------------------------------------------------------------

/** Користувач так, як його віддають різні ресурси: назва або складові імені. */
export interface UserLike {
    uuid?: string
    '@id'?: string
    title?: string
    shortName?: string
    fullName?: string
    firstName?: string
    lastName?: string
    /** Підписане посилання на фото (ADR 042) — там, де бек його віддає (вузол диска). */
    photoUrl?: string | null
}

/**
 * Ім'я людини так, як його пише бек у `shortName`: «Ткаченко Ірина» —
 * прізвище першим, як у легасі (`SPEC-005` §4.1, ВП-1 (а)).
 *
 * Скорочена група (`user:short:read` — журнали, коментарі) `shortName` не
 * віддає, лише складові; склеюємо їх у тому ж порядку, інакше та сама людина
 * в сусідніх блоках картки звалася то «Ірина Ткаченко», то «Ткаченко Ірина».
 */
export const userName = (user: UserLike): string =>
    user.shortName ??
    user.fullName ??
    user.title ??
    [user.lastName, user.firstName].filter(Boolean).join(' ')

/** Ініціали з імені: «Ковальчук Оксана» → «КО». */
export const initials = (name: string): string =>
    name
        .split(/\s+/)
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()

/**
 * Людина одним рядком: аватар і ім'я. Аватар — фото, якщо бек дав
 * `photoUrl`, інакше (і коли посилання вже не відкривається) — ініціали.
 */
export const UserChip = ({ user, size = 24 }: { user: UserLike; size?: number }) => {
    const { token } = theme.useToken()
    const name = userName(user)
    const photo = typeof user.photoUrl === 'string' && user.photoUrl !== '' ? user.photoUrl : undefined

    return (
        <span className={classNames(styles['person'])} title={name}>
            <Avatar
                size={size}
                {...(photo ? { src: photo, alt: name } : {})}
                style={{
                    background: token.colorPrimaryBg,
                    color: token.colorPrimary,
                    fontSize: Math.max(9, Math.round(size * 0.45)),
                    flexShrink: 0,
                }}
            >
                {initials(name)}
            </Avatar>
            <span className={classNames(styles['personName'])}>{name}</span>
        </span>
    )
}

/**
 * Один користувач або їх перелік — аватар з ініціалами і повне ім'я.
 *
 * Ім'я без позначки читається як довільний текст; аватар каже, що це людина,
 * ще до того, як прочитали ім'я, — і робить список виконавців сканованим.
 */
export const renderUsersView: FieldView = defineView((value) => {
    const users = (Array.isArray(value) ? value : [value]).filter(
        (user): user is UserLike => user !== null && typeof user === 'object'
    )

    if (users.length === 0) return '—'

    return (
        <span className={classNames(styles['people'])}>
            {users.map((user, index) => (
                <UserChip key={user.uuid ?? user['@id'] ?? index} user={user} />
            ))}
        </span>
    )
})

// ---- Дати --------------------------------------------------------------------

/** Дата в українському записі: «10.10.2026». */
export const formatDateUk = (value: unknown): string =>
    typeof value === 'string' && value !== '' && dayjs(value).isValid() ? dayjs(value).format(DATE_FORMAT) : '—'

/** Дата з часом: «22.09.2026, 13:57». */
export const formatDateTimeUk = (value: unknown): string =>
    typeof value === 'string' && value !== '' && dayjs(value).isValid()
        ? dayjs(value).format(DATETIME_FORMAT)
        : '—'

const MONTHS_GENITIVE = [
    'січня',
    'лютого',
    'березня',
    'квітня',
    'травня',
    'червня',
    'липня',
    'серпня',
    'вересня',
    'жовтня',
    'листопада',
    'грудня',
]

/**
 * Підпис дня у стрічці: «Сьогодні», «Вчора», «22 вересня», для минулих
 * років — з роком.
 *
 * Спільний для журналу змін і чату: один і той самий день у двох стрічках
 * застосунку не має зватися по-різному.
 */
export const formatDayLabel = (value: dayjs.ConfigType): string => {
    const day = dayjs(value).startOf('day')
    const today = dayjs().startOf('day')
    const diff = today.diff(day, 'day')

    if (diff === 0) return 'Сьогодні'
    if (diff === 1) return 'Вчора'

    const date = `${day.date()} ${MONTHS_GENITIVE[day.month()] ?? ''}`

    return day.year() === today.year() ? date : `${date} ${day.year()}`
}

/**
 * Дата в перегляді — по-українськи.
 *
 * Формат той самий, що в `AppConfig.dateDefaultFormat` (`Ship/Configs/DateFormat`).
 * Власний `view` у полів дати все одно потрібен: ядровий показ (`FormField`)
 * уже відформатоване значення ще раз розбирає `dayjs(…)`, а «14.10.2026» той
 * читає як місяць 14 — «Invalid Date» або переставлені день і місяць.
 */
export const renderDateView: FieldView = defineView((value) => formatDateUk(value))

export const renderDateTimeView: FieldView = defineView((value) => formatDateTimeUk(value))

// ---- Значення, які копіюють ------------------------------------------------------

/**
 * Перелік значень із кнопкою копіювання біля кожного — телефони, пошта.
 *
 * Їх здебільшого не читають, а переносять: у поштовий сервіс, месенджер,
 * документ. Копіюється рівно те, що показано (`format`), — щоб не було
 * сюрпризу, що вставилося щось інше.
 */
export const copyableListView = (format: (value: string) => string = (value) => value): FieldView =>
    defineView((value) => {
        const items = (Array.isArray(value) ? value : [value]).filter(
            (item): item is string => typeof item === 'string' && item !== ''
        )

        if (items.length === 0) return '—'

        return (
            <span className={classNames(styles['copyList'])}>
                {items.map((item) => {
                    const text = format(item)

                    return (
                        <Typography.Text key={item} copyable={{ text }}>
                            {text}
                        </Typography.Text>
                    )
                })}
            </span>
        )
    })

// ---- Числа -------------------------------------------------------------------

const asNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

/**
 * Кількість у перегляді — з розрядами й без зайвих нулів: «7 200», «4 930,2».
 *
 * Ядровий показ віддає число як є: «7200». Формат — спільний
 * `formatQuantity`, той самий, що в таблицях залишків і рухів.
 */
export const renderNumberView: FieldView = defineView((value) => formatQuantity(asNumber(value)))

/** Гроші в перегляді — завжди два знаки: «145,00». */
export const renderMoneyView: FieldView = defineView((value) => formatMoney(asNumber(value)))

/**
 * Дедлайн відносно сьогодні: скільки лишилося або наскільки прострочено.
 *
 * `closed` — документ уже завершено, і рахувати «прострочено» нема від чого.
 */
export const describeDeadline = (
    value: unknown,
    closed = false
): { text: string; tone: 'neutral' | 'warning' | 'error' | 'success' } => {
    const date = formatDateUk(value)

    if (date === '—' || closed) return { text: date, tone: 'neutral' }

    const days = dayjs(value as string)
        .startOf('day')
        .diff(dayjs().startOf('day'), 'day')

    if (days < 0) return { text: `${date} · прострочено на ${-days} дн.`, tone: 'error' }
    if (days === 0) return { text: `${date} · сьогодні`, tone: 'warning' }
    if (days === 1) return { text: `${date} · завтра`, tone: 'warning' }

    return { text: `${date} · через ${days} дн.`, tone: 'neutral' }
}
